import React, { useState, useEffect } from "react"
import { useMutation, useQuery } from "@apollo/client"
import { useParams, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import Button from "../../components/Button/Button"
import { RESET_PASSWORD } from "../../graphql/mutations"
import { VALIDATE_PASSWORD_RESET_LINK } from "../../graphql/queries"
import { useLoginModal } from "../../components/LoginModalContext"
import "./ResetPassword.css"

const ResetPassword = () => {
  const navigate = useNavigate()
  const params = useParams()
  const { openLogin } = useLoginModal()
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
  const authToken = useSelector((state) => state.auth.token)
  const user = useSelector((state) => state.mate.currentUser)
  const [resetToken, setResetToken] = useState("")
  const [tokenValid, setTokenValid] = useState(null)
  const [resetSuccess, setResetSuccess] = useState(false)
  const [newPassword, setNewPassword] = useState("")
  const [error, setError] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  
  // Naviger væk fra siden når brugeren logger ind
  useEffect(() => {
    const isLoggedIn = isAuthenticated && !!authToken && !!user
    if (isLoggedIn) {
      navigate("/")
    }
  }, [isAuthenticated, authToken, user, navigate])

  // Hent token fra URL parameter
  useEffect(() => {
    if (params.token) {
      setResetToken(params.token)
    }
  }, [params.token])

  // Valider password reset token
  const { loading: tokenLoading } = useQuery(VALIDATE_PASSWORD_RESET_LINK, {
    variables: { token: resetToken },
    skip: !resetToken,
    onCompleted: (data) => {
      if (data.validatePasswordResetLink && data.validatePasswordResetLink.isValid === true) {
        setTokenValid(true)
        setError("")
      } else {
        setTokenValid(false)
        setError("Ugyldigt eller udløbet password reset link")
      }
    },
    onError: (error) => {
      setTokenValid(false)
      setError("Ugyldigt password reset link: " + error.message)
    }
  })

  const [resetPasswordMutation, { loading: resetLoading }] = useMutation(RESET_PASSWORD, {
    onCompleted: (data) => {
      if (data.resetPassword && data.resetPassword.success) {
        setResetSuccess(true)
        setNewPassword(data.resetPassword.newPassword)
        setError("")
      } else {
        setError("Password reset fejlede")
      }
    },
    onError: (error) => {
      setError(error.message || "Password reset fejlede. Prøv igen.")
    }
  })

  const handleResetPassword = async (e) => {
    e.preventDefault()
    setError("")
    
    if (!resetToken || tokenValid !== true) {
      setError("Ugyldigt password reset link")
      return
    }
    
    try {
      await resetPasswordMutation({
        variables: {
          token: resetToken
        }
      })
    } catch (error) {
      setError("Password reset fejlede. Prøv igen.")
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const handleGoToLogin = () => {
    openLogin({ password: newPassword })
  }

  if (resetSuccess) {
    return (
      <div className="reset-password-background">
        <div className="reset-password-container">
          <h2 className="reset-password-title">Password Nulstillet!</h2>
          <div className="success-message">
            <p>Dit password er blevet nulstillet. Brug følgende password til at logge ind:</p>
            <div className="password-display">
              <input
                type={showPassword ? "text" : "password"}
                readOnly
                value={newPassword}
                className="password-input"
              />
              <button 
                type="button" 
                className="password-toggle" 
                onClick={togglePasswordVisibility}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {showPassword ? (
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                  ) : (
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor"/>
                  )}
                </svg>
              </button>
            </div>
            <p className="password-hint">Gem dette password sikkert. Du kan bruge det til at logge ind nu.</p>
          </div>
          <Button 
            title="Gå til Login" 
            onClick={handleGoToLogin} 
            variant="contained" 
            color="primary"
            style={{ width: '100%', marginTop: '16px' }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="reset-password-background">
      <div className="reset-password-container">
        <h2 className="reset-password-title">Nulstil Password</h2>
        {tokenLoading && (
          <div className="loading-message" style={{ color: '#3b82f6', marginBottom: '10px', textAlign: 'center' }}>
            Validerer password reset link...
          </div>
        )}
        {error && (
          <div className="error-message" style={{ color: '#ef4444', marginBottom: '10px', textAlign: 'center' }}>
            {error}
          </div>
        )}
        {tokenValid === false && !tokenLoading && (
          <div className="error-message" style={{ color: '#ef4444', marginBottom: '10px', textAlign: 'center' }}>
            Dette password reset link er ugyldigt eller udløbet.
          </div>
        )}
        {tokenValid === true && (
          <div className="info-message" style={{ color: '#3b82f6', marginBottom: '10px', textAlign: 'center' }}>
            Klik på knappen nedenfor for at nulstille dit password. Du vil modtage et nyt autogenereret password.
          </div>
        )}
        <form className="reset-password-form" onSubmit={handleResetPassword}>
          <Button 
            title={resetLoading ? "Nulstiller password..." : "Nulstil Password"} 
            onClick={handleResetPassword} 
            variant="contained" 
            color="primary"
            style={{ width: '100%', marginTop: '8px' }}
            disabled={resetLoading || tokenLoading || tokenValid !== true}
          />
        </form>
      </div>
    </div>
  )
}

export default ResetPassword

